import { Injectable, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';

type TokenShape = {
  permissions?: Array<string | { name?: string; permission?: string; value?: string }>;
  authorities?: string[];
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
  scope?: string;
  scp?: string[] | string;
};

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly keycloak = inject(Keycloak);

  isAuthenticated(): boolean {
    try {
      return Boolean(this.keycloak.authenticated && !this.keycloak.isTokenExpired(5));
    } catch {
      return false;
    }
  }

  async ensureAuthenticated(): Promise<boolean> {
    if (this.isAuthenticated()) {
      return true;
    }

    await this.keycloak.login({ redirectUri: window.location.href });
    return false;
  }

  hasPermission(permission: string): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }

    return this.getPermissions().has(permission);
  }

  private getPermissions(): Set<string> {
    const token = (this.keycloak.tokenParsed ?? {}) as TokenShape;
    const permissions = new Set<string>();

    for (const entry of token.permissions ?? []) {
      if (typeof entry === 'string') {
        permissions.add(entry);
      } else {
        const value = entry.permission ?? entry.name ?? entry.value;
        if (value) {
          permissions.add(value);
        }
      }
    }

    for (const authority of token.authorities ?? []) {
      permissions.add(authority);
    }

    for (const role of token.realm_access?.roles ?? []) {
      permissions.add(role);
    }

    for (const role of token.resource_access?.[environment.keycloak.clientId]?.roles ?? []) {
      permissions.add(role);
    }

    const scopeClaim = token.scope;
    if (scopeClaim) {
      for (const scope of scopeClaim.split(' ')) {
        if (scope) {
          permissions.add(scope);
        }
      }
    }

    const scpClaim = token.scp;
    if (Array.isArray(scpClaim)) {
      for (const scope of scpClaim) {
        permissions.add(scope);
      }
    } else if (typeof scpClaim === 'string') {
      for (const scope of scpClaim.split(' ')) {
        if (scope) {
          permissions.add(scope);
        }
      }
    }

    return permissions;
  }
}